import React, { useState, useEffect } from 'react';
import { Form, message } from 'antd';
import {
  StyledRatingFormEdit,
  StyledRatingDrawerFooter,
  StyledRatingDrawerActions,
  StyledRatingWarning,
} from './style';
import { Button, Divider } from 'ui';
import { TextArea } from 'ui/TextArea';
import StarRating from '../../StarRating';
import useQueryApiClient from '../../../utils/useQueryApiClient';
import { useIntl } from 'react-intl';

interface Rating {
  id: number;
  transaction_id: number;
  product_quality_rating: number | null;
  delivery_rating: number | null;
  communication_rating: number | null;
  payments_rating: number | null;
  comments: string | null;
}

interface RatingPayload {
  transaction_id?: number;
  product_quality_rating?: number | null;
  delivery_rating?: number | null;
  communication_rating?: number | null;
  payments_rating?: number | null;
  comments: string | null;
}

interface RatingDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  transactionId: number;
  transactionStatus: string;
  existingRating?: Rating | null;
  viewOnly?: boolean;
}

const RatingDrawer = ({
  isOpen,
  onClose,
  transactionId,
  transactionStatus,
  existingRating,
  viewOnly = false,
}: RatingDrawerProps) => {
  const [form] = Form.useForm();
  const intl = useIntl();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isCompleted = ['delivered', 'completed'].includes(transactionStatus?.toLowerCase());
  const isCancelled = ['canceled', 'cancelled', 'failed', 'rejected'].includes(transactionStatus?.toLowerCase());
  const showRatings = isCompleted && !viewOnly;
  const showOnlyComments = isCancelled;

  useEffect(() => {
    if (existingRating) {
      form.setFieldsValue({
        product_quality_rating: existingRating.product_quality_rating,
        delivery_rating: existingRating.delivery_rating,
        communication_rating: existingRating.communication_rating,
        payments_rating: existingRating.payments_rating,
        comments: existingRating.comments,
      });
    } else {
      form.resetFields();
    }
  }, [existingRating, form, isOpen]);

  const { appendData: saveRating } = useQueryApiClient({
    request: {
      url: existingRating?.id ? `api/v2/ratings/${existingRating.id}` : 'api/v2/ratings',
      method: existingRating?.id ? 'PATCH' : 'POST',
    },
    onSuccess: () => {
      message.success(
        intl.formatMessage({
          id: existingRating?.id ? 'rating.updated_successfully' : 'rating.created_successfully',
        })
      );
      setIsSubmitting(false);
      onClose();
    },
    onError: () => {
      message.error(
        intl.formatMessage({ id: 'rating.save_error' })
      );
      setIsSubmitting(false);
    },
  });

  const { appendData: deleteRating } = useQueryApiClient({
    request: {
      url: 'api/v2/ratings/:id',
      method: 'DELETE',
    },
    onSuccess: () => {
      message.success(
        intl.formatMessage({ id: 'rating.deleted_successfully' })
      );
      onClose();
    },
    onError: () => {
      message.error(
        intl.formatMessage({ id: 'rating.delete_error' })
      );
    },
  });

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setIsSubmitting(true);

      const payload: RatingPayload = {
        comments: values.comments || null,
      };

      if (!existingRating?.id) {
        payload.transaction_id = transactionId;
      }

      if (showRatings) {
        payload.product_quality_rating = values.product_quality_rating || null;
        payload.delivery_rating = values.delivery_rating || null;
        payload.communication_rating = values.communication_rating || null;
        payload.payments_rating = values.payments_rating || null;
      }

      saveRating(payload);
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const handleDelete = () => {
    if (existingRating?.id) {
      deleteRating([], {id: existingRating?.id})
    }
  };

  const footer = viewOnly ? null : (
    <StyledRatingDrawerFooter>
      {existingRating?.id && (
        <Button
          type="default"
          danger
          label={intl.formatMessage({ id: 'general.delete' })}
          onClick={handleDelete}
        />
      )}
      <StyledRatingDrawerActions>
        <Button
          type="default"
          label={intl.formatMessage({ id: 'general.cancel' })}
          onClick={onClose}
        />
        <Button
          type="primary"
          label={intl.formatMessage({ id: 'general.save' })}
          onClick={handleSubmit}
          loading={isSubmitting}
        />
      </StyledRatingDrawerActions>
    </StyledRatingDrawerFooter>
  );

  return (
    <StyledRatingFormEdit
      width={600}
      open={isOpen}
      onClose={onClose}
      title={
        viewOnly
          ? intl.formatMessage({ id: 'rating.view_title' })
          : existingRating?.id
          ? intl.formatMessage({ id: 'rating.edit_title' })
          : intl.formatMessage({ id: 'rating.give_title' })
      }
      footer={footer}
      destroyOnClose
      zIndex={1300}
    >
      <Form form={form}>
        {showRatings && (
          <>
            <div className={'rating-wrapper'}>
              <StarRating
                label={intl.formatMessage({ id: 'rating.product_quality' })}
                name="product_quality_rating"
                disabled={viewOnly}
              />
              <Divider marginBottom={0} marginTop={0}/>
              <StarRating
                label={intl.formatMessage({ id: 'rating.delivery' })}
                name="delivery_rating"
                disabled={viewOnly}
              />
              <Divider marginBottom={0} marginTop={0}/>
              <StarRating
                label={intl.formatMessage({ id: 'rating.communication' })}
                name="communication_rating"
                disabled={viewOnly}
              />
              <Divider marginBottom={0} marginTop={0}/>
              <StarRating
                label={intl.formatMessage({ id: 'rating.payments' })}
                name="payments_rating"
                disabled={viewOnly}
              />
            </div>
          </>
        )}
        <div className={'rating-comment'}>
          <TextArea
            label={intl.formatMessage({ id: 'rating.comments' })}
            name="comments"
            disabled={viewOnly}
            placeholder={
              intl.formatMessage({ id: 'rating.comments_placeholder' })
            }
            maxLength={1000}
          />
        </div>

        {showOnlyComments && !showRatings && (
          <StyledRatingWarning>
            {intl.formatMessage({ id: 'rating.cancelled_info' })}
          </StyledRatingWarning>
        )}
      </Form>
    </StyledRatingFormEdit>
  );
};

export default RatingDrawer;